/**
 * Game Board Component
 * Main game container with organic button positioning
 */

import { useState, useEffect, useRef } from 'react';
import { GameEngine, GameMode, GameState } from '../core/GameEngine';
import { SequenceManager, ButtonColor, IntruderButton, RhythmFeedback } from '../core/SequenceManager';
import { ScoreSystem } from '../core/ScoreSystem';
import { AudioEngine } from '../core/AudioEngine';
import { PhysicsEngine } from '../core/PhysicsEngine';
import { ButtonWearSystem } from '../core/ButtonWearSystem';
import { PanicModeSystem } from '../core/PanicModeSystem';
import { RhythmEngine } from '../core/RhythmEngine';
import { Button, ButtonPosition } from './Button';
import { GameOverScreen } from './GameOverScreen';
import { TapToStartOverlay } from './TapToStartOverlay';
import { RhythmTimeline } from './RhythmTimeline';

const COLORS: ButtonColor[] = ['red', 'green', 'blue', 'yellow'];
const SHOW_DURATION = 600; // ms per button in sequence
const BUTTON_SIZE = 80;
const BUTTON_PADDING = 20;

interface GameBoardProps {
  initialMode: GameMode;
  onBackToModeSelect?: () => void;
}

export function GameBoard({ initialMode, onBackToModeSelect }: GameBoardProps) {
  const engineRef = useRef(new GameEngine());
  const sequenceRef = useRef(new SequenceManager());
  const scoreRef = useRef(new ScoreSystem());
  const audioRef = useRef(new AudioEngine());
  const physicsRef = useRef(new PhysicsEngine());
  const wearRef = useRef(new ButtonWearSystem());
  const panicRef = useRef(new PanicModeSystem());
  const rhythmEngineRef = useRef(new RhythmEngine());
  
  const [audioInitialized, setAudioInitialized] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [gameState, setGameState] = useState<GameState>('idle');
  const [selectedMode] = useState<GameMode>(initialMode);
  const [positions, setPositions] = useState<Record<ButtonColor, ButtonPosition>>({} as any);
  const [activeButton, setActiveButton] = useState<ButtonColor | null>(null);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [boardSize, setBoardSize] = useState({ width: 0, height: 0 });
  const [invisibleMode, setInvisibleMode] = useState(false);
  const [revealedButtons, setRevealedButtons] = useState<Set<ButtonColor>>(new Set());
  const [buttonWear, setButtonWear] = useState<Map<ButtonColor, number>>(new Map());
  // Color mapping for identity migration: physical position -> displayed color
  const [colorMapping, setColorMapping] = useState<Map<ButtonColor, ButtonColor>>(new Map());
  
  // Rhythm mode state
  const [rhythmFeedback, setRhythmFeedback] = useState<RhythmFeedback | null>(null);
  const sequenceTimestamps = useRef<number[]>([]);
  const feedbackTimeoutRef = useRef<number | null>(null);
  const [rhythmCurrentTime, setRhythmCurrentTime] = useState(0);
  const rhythmAnimationRef = useRef<number>();
  const kickScheduleRef = useRef<any[]>([]);
  const [countInActive, setCountInActive] = useState(false);
  
  // Panic mode state
  const [panicMode, setPanicMode] = useState(false);
  const [panicStreak, setPanicStreak] = useState(0);
  
  // Intruder system state
  const [intruders, setIntruders] = useState<IntruderButton[]>([]);
  const intruderSpawnTimerRef = useRef<number | null>(null);
  const intruderIdCounterRef = useRef(0);
  const animationFrameRef = useRef<number>();

  // Apply initial mode on mount
  useEffect(() => {
    applyMode(initialMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize board size and physics
  useEffect(() => {
    const updateSize = () => {
      const width = Math.min(window.innerWidth - 40, 400);
      const height = Math.min(window.innerHeight - 200, 400);
      setBoardSize({ width, height });
      
      // Initialize physics with new dimensions
      if (width > 0 && height > 0) {
        physicsRef.current.init(width, height, BUTTON_SIZE / 2);
      }
    };
    
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Physics animation loop, active only in chaos mode
  useEffect(() => {
    const physicsEnabled = engineRef.current.getConfig().enablePhysics;

    const updatePhysics = () => {
      const bodies = physicsRef.current.getAllBodies();
      const newPositions: Record<ButtonColor, ButtonPosition> = {} as any;

      bodies.forEach((body, color) => {
        newPositions[color] = {
          x: body.position.x - BUTTON_SIZE / 2,
          y: body.position.y - BUTTON_SIZE / 2
        };
      });

      setPositions(newPositions);
      animationFrameRef.current = requestAnimationFrame(updatePhysics);
    };

    if (physicsEnabled && gameState !== 'idle' && Object.keys(positions).length > 0) {
      physicsRef.current.start();
      animationFrameRef.current = requestAnimationFrame(updatePhysics);
    } else {
      physicsRef.current.stop();
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [gameState, positions]);

  // Intruder spawning system - only during waitInput phase
  useEffect(() => {
    // Clear any existing spawn timer
    if (intruderSpawnTimerRef.current) {
      clearInterval(intruderSpawnTimerRef.current);
      intruderSpawnTimerRef.current = null;
    }

    // Clear intruders when not in input phase
    if (gameState !== 'waitInput') {
      setIntruders([]);
      return;
    }

    // Check if intruders are enabled
    const config = engineRef.current.getConfig();
    const intruderConfig = config.intruderConfig;
    if (!config.enableIntruders || !intruderConfig?.enabled) {
      return;
    }

    // Spawn intruders randomly during input phase
    const spawnChance = engineRef.current.getIntruderSpawnChance(level);
    const checkInterval = 1000; // Check every second

    intruderSpawnTimerRef.current = setInterval(() => {
      if (Math.random() < spawnChance && intruders.length < (intruderConfig.maxIntruders || 2)) {
        spawnIntruder();
      }
    }, checkInterval);

    return () => {
      if (intruderSpawnTimerRef.current) {
        clearInterval(intruderSpawnTimerRef.current);
        intruderSpawnTimerRef.current = null;
      }
    };
  }, [gameState, level, intruders.length]);

  // Rhythm mode animation loop - updates current time for timeline
  useEffect(() => {
    if (gameState !== 'waitInput' || !engineRef.current.getConfig().enableRhythm) {
      // Stop rhythm loop when leaving waitInput
      if (kickScheduleRef.current.length > 0 && typeof kickScheduleRef.current[0] === 'function') {
        kickScheduleRef.current[0](); // Call stop function
        kickScheduleRef.current = [];
      }
      audioRef.current.stopRhythmLoop();
      
      if (rhythmAnimationRef.current) {
        cancelAnimationFrame(rhythmAnimationRef.current);
        rhythmAnimationRef.current = undefined;
      }
      return;
    }

    const updateRhythmTime = () => {
      setRhythmCurrentTime(rhythmEngineRef.current.getCurrentTime());
      rhythmAnimationRef.current = requestAnimationFrame(updateRhythmTime);
    };

    rhythmAnimationRef.current = requestAnimationFrame(updateRhythmTime);

    return () => {
      if (rhythmAnimationRef.current) {
        cancelAnimationFrame(rhythmAnimationRef.current);
        rhythmAnimationRef.current = undefined;
      }
      audioRef.current.stopRhythmLoop();
    };
  }, [gameState]);

  // Spawn a single intruder at random position
  const spawnIntruder = () => {
    const colors: ButtonColor[] = ['red', 'green', 'blue', 'yellow'];
    const randomColor = colors[Math.floor(Math.random() * colors.length)];
    
    // Find a position that doesn't overlap with existing buttons or intruders
    let attempts = 0;
    let pos: ButtonPosition;
    const usedPositions = [
      ...Object.values(positions),
      ...intruders.map(i => i.position)
    ];

    do {
      pos = {
        x: Math.random() * (boardSize.width - BUTTON_SIZE),
        y: Math.random() * (boardSize.height - BUTTON_SIZE)
      };
      attempts++;
    } while (attempts < 30 && usedPositions.some(used => {
      const dx = used.x - pos.x;
      const dy = used.y - pos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      return distance < BUTTON_SIZE + BUTTON_PADDING;
    }));

    const newIntruder: IntruderButton = {
      id: `intruder-${intruderIdCounterRef.current++}`,
      color: randomColor,
      position: pos,
      isDecoy: true,
      spawnTime: Date.now()
    };

    setIntruders(prev => [...prev, newIntruder]);

    // Auto-remove after 3-5 seconds if not clicked
    const lifespan = 3000 + Math.random() * 2000;
    setTimeout(() => {
      setIntruders(prev => prev.filter(i => i.id !== newIntruder.id));
    }, lifespan);
  };


  // Generate random non-overlapping positions and create physics bodies when chaos mode is active
  const generatePositions = (): Record<ButtonColor, ButtonPosition> => {
    const positions: Record<ButtonColor, ButtonPosition> = {} as any;
    const usedPositions: ButtonPosition[] = [];

    const isOverlapping = (pos: ButtonPosition): boolean => {
      return usedPositions.some(used => {
        const dx = used.x - pos.x;
        const dy = used.y - pos.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        return distance < BUTTON_SIZE + BUTTON_PADDING;
      });
    };

    const physicsEnabled = engineRef.current.getConfig().enablePhysics;

    // Remove old physics bodies
    COLORS.forEach(color => physicsRef.current.removeButton(color));

    for (const color of COLORS) {
      let attempts = 0;
      let pos: ButtonPosition;
      
      do {
        pos = {
          x: Math.random() * (boardSize.width - BUTTON_SIZE),
          y: Math.random() * (boardSize.height - BUTTON_SIZE)
        };
        attempts++;
      } while (isOverlapping(pos) && attempts < 50);
      
      positions[color] = pos;
      usedPositions.push(pos);
      
      if (physicsEnabled) {
        const centerX = pos.x + BUTTON_SIZE / 2;
        const centerY = pos.y + BUTTON_SIZE / 2;
        const angle = Math.random() * Math.PI * 2;
        const speed = 1 + Math.random() * 2;
        physicsRef.current.createButton(
          color,
          centerX,
          centerY,
          Math.cos(angle) * speed,
          Math.sin(angle) * speed
        );
      }
    }

    return positions;
  };

  // Start new game
  const startGame = () => {
    // Always show sequence first (both classic and rhythm modes)
    engineRef.current.setState('showSequence');
    setGameState('showSequence');
    
    scoreRef.current.reset();
    sequenceRef.current.generate(1);
    panicRef.current.reset();
    setPanicMode(false);
    setPanicStreak(0);

    const initialScore = scoreRef.current.getState();
    setScore(initialScore.current);
    setHighScore(initialScore.highScore);
    setLevel(initialScore.level);
    setPositions(generatePositions());
    
    // Initialize button wear system if enabled
    if (engineRef.current.getConfig().enableWear) {
      wearRef.current.initialize(COLORS);
      updateWearDisplay();
    }

    showSequence();
  };

  // Play count-in before rhythm input starts (4 kicks = 1 measure)
  const playCountIn = async (): Promise<void> => {
    const BEAT_INTERVAL = 500; // 120 BPM
    const COUNT_IN_BEATS = 4; // 1 measure = 4 beats
    
    // Show count-in indicator
    setCountInActive(true);
    
    // Ensure AudioContext is active before starting
    await audioRef.current.resume();
    
    // Play 4 count-in kicks (louder and more pronounced)
    for (let i = 0; i < COUNT_IN_BEATS; i++) {
      if (audioRef.current.ready) {
        audioRef.current.playCountInKick(); // ← Use dedicated count-in kick
      }
      await new Promise(resolve => setTimeout(resolve, BEAT_INTERVAL));
    }
    
    // Hide count-in indicator
    setCountInActive(false);
  };

  // Start rhythm mode session (after showing sequence)
  const startRhythmMode = () => {
    // Get the sequence length (player must remember colors)
    const sequence = sequenceRef.current.getSequence();
    const sequenceLength = sequence.length;
    
    // Start rhythm engine with just the length (no color info)
    rhythmEngineRef.current.start(sequenceLength);
    
    // Start audio loop: kick every 500ms, snare every 2000ms
    const stopRhythmLoop = audioRef.current.startRhythmLoop();
    
    // Store stop function for cleanup
    kickScheduleRef.current = [stopRhythmLoop as any];
    
    // Start panic mode timing if enabled
    if (engineRef.current.getConfig().enablePanic) {
      sequenceRef.current.startInputPhase();
    }
  };

  // Show sequence to player
  const showSequence = async () => {
    const sequence = sequenceRef.current.getSequence();
    
    // Before showing sequence, make buttons visible again
    setInvisibleMode(false);
    setRevealedButtons(new Set());
    
    // Clear rhythm feedback
    setRhythmFeedback(null);
    sequenceTimestamps.current = [];
    
    const isRhythmMode = engineRef.current.getConfig().enableRhythm;
    const beatInterval = engineRef.current.getConfig().rhythmConfig?.beatIntervalMs || 600;
    const panicEnabled = engineRef.current.getConfig().enablePanic;
    
    // Apply panic speed multiplier to sequence display
    const speedMultiplier = panicEnabled && panicRef.current.isActive()
      ? panicRef.current.getSpeedMultiplier()
      : 1.0;
    
    for (let i = 0; i < sequence.length; i++) {
      const step = sequence[i];
      setActiveButton(step.color);
      
      // Track expected timestamp for rhythm mode relative to input start
      if (isRhythmMode) {
        sequenceTimestamps.current.push(i * beatInterval);
      }
      
      // Play audio with positional panning
      if (audioRef.current.ready) {
        const pos = positions[step.color];
        if (pos) {
          audioRef.current.playTone(step.color, pos.x + BUTTON_SIZE / 2, boardSize.width, 300);
        }
      }
      
      // In rhythm mode, use beatInterval; otherwise use SHOW_DURATION
      // Apply speed multiplier for panic mode
      const baseDuration = isRhythmMode ? beatInterval : SHOW_DURATION;
      const duration = Math.floor(baseDuration / speedMultiplier);
      
      await new Promise(resolve => setTimeout(resolve, duration / 2));
      setActiveButton(null);
      await new Promise(resolve => setTimeout(resolve, duration / 2));
    }

    // For rhythm mode: play count-in before starting input
    if (isRhythmMode) {
      // Set state to countIn temporarily
      engineRef.current.setState('countIn');
      setGameState('countIn');
      
      // Play count-in (4 kicks, 2000ms)
      await playCountIn();
    }

    // Transition to input phase
    engineRef.current.setState('waitInput');
    setGameState('waitInput');
    sequenceRef.current.reset();
    
    // For rhythm mode, start rhythm engine with sequence
    if (isRhythmMode) {
      startRhythmMode();
    }
    
    // Start panic mode timing if enabled
    if (engineRef.current.getConfig().enablePanic) {
      sequenceRef.current.startInputPhase();
    }

    // Activate invisible mode if enabled (after showing sequence)
    if (engineRef.current.getConfig().enableInvisible) {
      // Short delay before fading out
      setTimeout(() => {
        setInvisibleMode(true);
      }, 300);
    }
  };

  // Update wear display from wear system
  const updateWearDisplay = () => {
    const wearMap = new Map<ButtonColor, number>();
    COLORS.forEach(color => {
      const wear = wearRef.current.getWear(color);
      if (wear) {
        wearMap.set(color, wear.wearLevel);
      }
    });
    setButtonWear(wearMap);
  };

  // Handle proximity reveal for invisible mode
  const handleProximityReveal = (color: ButtonColor, reveal: boolean) => {
    setRevealedButtons(prev => {
      const next = new Set(prev);
      if (reveal) {
        next.add(color);
      } else {
        next.delete(color);
      }
      return next;
    });
  };

  // Handle player input
  const handleButtonPress = (color: ButtonColor, isDecoy?: boolean, intruderId?: string) => {
    if (gameState !== 'waitInput') return;

    // Check if this is an intruder button
    if (isDecoy) {
      const intruderConfig = engineRef.current.getConfig().intruderConfig;
      
      // Remove the clicked intruder
      setIntruders(prev => prev.filter(i => i.id !== intruderId));
      
      // Apply penalty
      if (intruderConfig?.penaltyType === 'end') {
        // End game immediately
        engineRef.current.setState('gameOver');
        setGameState('gameOver');
      } else if (intruderConfig?.penaltyType === 'deduct') {
        // Deduct points
        const penalty = intruderConfig.penaltyAmount || 50;
        scoreRef.current.deductPoints(penalty);
        const newScore = scoreRef.current.getState();
        setScore(newScore.current);
        
        // Play error sound
        if (audioRef.current.ready) {
          audioRef.current.playTone('red', boardSize.width / 2, boardSize.width, 100);
        }
      }
      return;
    }

    // Visual feedback
    setActiveButton(color);
    setTimeout(() => setActiveButton(null), 200);
    
    // Audio feedback
    if (audioRef.current.ready) {
      const pos = positions[color];
      if (pos) {
        audioRef.current.playTone(color, pos.x + BUTTON_SIZE / 2, boardSize.width, 200);
      }
    }

    // Validate input (rhythm mode vs classic mode)
    const isRhythmMode = engineRef.current.getConfig().enableRhythm;
    const panicEnabled = engineRef.current.getConfig().enablePanic;
    let isCorrect = false;

    // Record response time for panic mode (before validation)
    let responseTime = 0;
    if (panicEnabled) {
      responseTime = sequenceRef.current.recordResponseTime();
    }

    if (isRhythmMode) {
      // NEW RHYTHM MODE VALIDATION:
      // 1. Check if player tapped the correct COLOR (from memory)
      // 2. Check if player tapped at the correct TIMING (within window)
      
      const sequence = sequenceRef.current.getSequence();
      const nextTarget = rhythmEngineRef.current.getNextTapTarget();
      
      if (!nextTarget) {
        // No more timing targets
        isCorrect = false;
      } else {
        // Get expected color from sequence based on rhythm engine progress
        const currentProgress = rhythmEngineRef.current.getProgress();
        const currentIndex = Math.floor(currentProgress * sequence.length);
        const expectedColor = sequence[currentIndex]?.color;
        
        // Validate color first
        if (color !== expectedColor) {
          isCorrect = false;
        } else {
          // Color correct, now validate timing
          const tapFeedback = rhythmEngineRef.current.validateTap();
          isCorrect = tapFeedback.success;
          
          // Convert to RhythmFeedback for display
          const rhythmFeedback: RhythmFeedback = {
            precision: tapFeedback.timing === 'perfect' ? 'perfect' :
                       tapFeedback.timing === 'good' ? 'good' :
                       tapFeedback.timing === 'early' || tapFeedback.timing === 'late' ? 'ok' : 'miss',
            delta: tapFeedback.delta,
          };
          
          setRhythmFeedback(rhythmFeedback);
          
          // Clear feedback after animation
          if (feedbackTimeoutRef.current) {
            clearTimeout(feedbackTimeoutRef.current);
          }
          feedbackTimeoutRef.current = setTimeout(() => {
            setRhythmFeedback(null);
          }, 1000) as unknown as number;
          
          // Advance sequence manually if timing was correct
          if (isCorrect) {
            sequenceRef.current.validate(color);
          }
        }
      }
    } else {
      // Classic validation
      isCorrect = sequenceRef.current.validate(color);
    }

    if (!isCorrect) {
      // Game over
      engineRef.current.setState('gameOver');
      setGameState('gameOver');
      return;
    }

    // Update panic mode state if enabled
    if (panicEnabled) {
      const isRhythmMode = engineRef.current.getConfig().enableRhythm;
      const beatInterval = engineRef.current.getConfig().rhythmConfig?.beatIntervalMs || 600;
      // Available time is the show duration for each button
      const availableTime = isRhythmMode ? beatInterval : SHOW_DURATION;
      
      const panicConfig = engineRef.current.getConfig().panicConfig!;
      panicRef.current.updateConfig(panicConfig);
      
      panicRef.current.recordResponse(responseTime, availableTime);
      const panicState = panicRef.current.getState();
      
      setPanicMode(panicState.active);
      setPanicStreak(panicState.streak);
    }

    // Track button wear if enabled
    const wearEnabled = engineRef.current.getConfig().enableWear;
    if (wearEnabled) {
      const stillUsable = wearRef.current.useButton(color);
      updateWearDisplay();
      
      // Check if button just became exhausted
      if (!stillUsable) {
        // Find migration target
        const target = wearRef.current.findMigrationTarget(color);
        if (target) {
          // Migrate identity: the exhausted button position takes on a new color/identity
          setTimeout(() => {
            // Visual transition effect
            setActiveButton(color);
            setTimeout(() => {
              // Update color mapping so the exhausted position now displays the target color
              setColorMapping(prev => {
                const next = new Map(prev);
                // Find what color is currently at the target position
                const currentTargetDisplay = next.get(target) || target;
                // Swap: exhausted position gets target's current display color
                next.set(color, currentTargetDisplay);
                // Reset target to show its original color (it's taking over the exhausted identity)
                next.delete(target);
                return next;
              });
              
              // Update wear system
              wearRef.current.migrateIdentity(color, target);
              updateWearDisplay();
              setActiveButton(null);
            }, 300);
          }, 400);
        }
      }
    }

    // Check if sequence complete (different logic for rhythm vs classic)
    const isSequenceComplete = isRhythmMode 
      ? rhythmEngineRef.current.isComplete()
      : sequenceRef.current.isComplete();
      
    if (isSequenceComplete) {
      const completedLength = isRhythmMode 
        ? rhythmEngineRef.current.getTapTargets().length
        : sequenceRef.current.getLength();

      // Get panic multiplier if active
      const panicMultiplier = panicEnabled && panicRef.current.isActive() 
        ? panicRef.current.getScoreMultiplier() 
        : 1.0;

      // Official scoring formula: sequenceLength * 10 (with optional panic multiplier)
      scoreRef.current.addPointsForSequence(completedLength, panicMultiplier);
      scoreRef.current.levelUp();

      const newScore = scoreRef.current.getState();
      setScore(newScore.current);
      setHighScore(newScore.highScore);
      setLevel(newScore.level);

      // Stop current rhythm session if rhythm mode
      if (isRhythmMode) {
        rhythmEngineRef.current.stop();
      }

      // Extend sequence and reposition buttons
      if (!isRhythmMode) {
        sequenceRef.current.extend();
      }
      setPositions(generatePositions());

      // Show next sequence after delay (with panic speed multiplier)
      const speedMultiplier = panicEnabled && panicRef.current.isActive()
        ? panicRef.current.getSpeedMultiplier()
        : 1.0;
      const transitionDelay = Math.floor(800 / speedMultiplier);
      
      engineRef.current.setState('transition');
      setGameState('transition');
      
      setTimeout(() => {
        if (isRhythmMode) {
          engineRef.current.setState('waitInput');
          setGameState('waitInput');
          startRhythmMode();
        } else {
          engineRef.current.setState('showSequence');
          setGameState('showSequence');
          showSequence();
        }
      }, transitionDelay);
    }
  };

  // Restart game
  const handleRestart = () => {
    startGame();
  };

  const applyMode = (mode: GameMode) => {
    // Note: selectedMode is now a prop, not state, so we just configure the engine

    if (mode === 'chaos') {
      engineRef.current.updateConfig({
        mode,
        enablePhysics: true,
        enableIntruders: true,
        enableInvisible: true,
        enableRhythm: false,
        enableWear: false,
        enablePanic: false,
        intruderConfig: {
          ...engineRef.current.getConfig().intruderConfig!,
          enabled: true
        },
        rhythmConfig: {
          ...engineRef.current.getConfig().rhythmConfig!,
          enabled: false
        },
        wearConfig: {
          ...engineRef.current.getConfig().wearConfig!,
          enabled: false
        },
        panicConfig: {
          ...engineRef.current.getConfig().panicConfig!,
          enabled: false
        }
      });
    } else if (mode === 'rhythm') {
      engineRef.current.updateConfig({
        mode,
        enablePhysics: false,
        enableIntruders: false,
        enableInvisible: false,
        enableRhythm: true,
        enableWear: true, // Enable button wear in rhythm mode
        enablePanic: true, // Enable panic mode in rhythm mode
        intruderConfig: {
          ...engineRef.current.getConfig().intruderConfig!,
          enabled: false
        },
        rhythmConfig: {
          ...engineRef.current.getConfig().rhythmConfig!,
          enabled: true
        },
        wearConfig: {
          ...engineRef.current.getConfig().wearConfig!,
          enabled: true
        },
        panicConfig: {
          ...engineRef.current.getConfig().panicConfig!,
          enabled: true
        }
      });
      setIntruders([]);
      setInvisibleMode(false);
      setRevealedButtons(new Set());
      // Initialize wear system for rhythm mode
      if (gameState === 'idle' || gameState === 'waitInput') {
        wearRef.current.initialize(COLORS);
        updateWearDisplay();
      }
    } else {
      engineRef.current.updateConfig({
        mode,
        enablePhysics: false,
        enableIntruders: false,
        enableInvisible: false,
        enableRhythm: false,
        enableWear: false,
        enablePanic: false,
        intruderConfig: {
          ...engineRef.current.getConfig().intruderConfig!,
          enabled: false
        },
        rhythmConfig: {
          ...engineRef.current.getConfig().rhythmConfig!,
          enabled: false
        },
        wearConfig: {
          ...engineRef.current.getConfig().wearConfig!,
          enabled: false
        },
        panicConfig: {
          ...engineRef.current.getConfig().panicConfig!,
          enabled: false
        }
      });
      setIntruders([]);
      setInvisibleMode(false);
      setRevealedButtons(new Set());
    }

    if (Object.keys(positions).length > 0) {
      setPositions(generatePositions());
    }
  };

  // Initialize audio and start game
  const handleTapToStart = async () => {
    try {
      await audioRef.current.init();
      setErrorMessage(null);
      setAudioInitialized(true);

      if (boardSize.width > 0 && boardSize.height > 0) {
        startGame();
      }
    } catch (err) {
      console.error('Audio initialization failed:', err);
      setErrorMessage('Audio could not start. The game will continue without sound.');
      setAudioInitialized(true);
      if (boardSize.width > 0 && boardSize.height > 0) {
        startGame();
      }
    }
  };

  // Resume audio when window regains focus
  useEffect(() => {
    const handleFocus = () => {
      if (audioRef.current.ready) {
        audioRef.current.resume();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      audioRef.current.dispose();
      physicsRef.current.dispose();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (feedbackTimeoutRef.current) {
        clearTimeout(feedbackTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      {/* Tap to start overlay - required for Web Audio API */}
      {!audioInitialized && (
        <TapToStartOverlay onTap={handleTapToStart} />
      )}

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        width: '100%',
        padding: 'max(12px, env(safe-area-inset-top)) 12px max(12px, env(safe-area-inset-bottom))',
        gap: '16px'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px'
        }}>
          <h1 style={{
            fontFamily: 'Syne, sans-serif',
            fontSize: '32px',
            fontWeight: 800,
            letterSpacing: '-1px',
            color: 'var(--accent-b)',
            margin: 0
          }}>
            SIMON REVOLUTION
          </h1>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '12px 20px',
            fontSize: '14px',
            color: 'var(--muted)'
          }}>
            <div>Level: <span style={{ color: 'var(--text)', fontWeight: 600 }}>{level}</span></div>
            <div>Score: <span style={{ color: 'var(--accent-g)', fontWeight: 600 }}>{score}</span></div>
            <div>Best: <span style={{ color: 'var(--accent-y)', fontWeight: 600 }}>{highScore}</span></div>
          </div>
        </div>

        {/* Game Board */}
        <div style={{
          position: 'relative',
          width: `min(${boardSize.width}px, 100%)`,
          height: `${boardSize.height}px`,
          maxWidth: '400px',
          background: 'var(--surface)',
          border: '2px solid var(--border)',
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 12px 40px rgba(0,0,0,0.28)'
        }}>
          {Object.entries(positions).map(([color, pos]) => {
            const physicalColor = color as ButtonColor;
            const displayColor = colorMapping.get(physicalColor) || physicalColor;
            const isRevealed = revealedButtons.has(physicalColor);
            const wear = buttonWear.get(physicalColor) || 0;

            return (
              <Button
                key={color}
                color={displayColor}
                position={pos}
                isActive={activeButton === physicalColor || isRevealed}
                isShowingSequence={gameState === 'showSequence'}
                isInvisible={invisibleMode && !isRevealed}
                wearLevel={wear}
                onPress={(_, isDecoy, intruderId) => {
                  // When pressed, use the display color (what the player sees)
                  handleButtonPress(displayColor, isDecoy, intruderId);
                }}
                onProximityReveal={(_, reveal) => handleProximityReveal(physicalColor, reveal)}
              />
            );
          })}

          {intruders.map((intruder) => (
            <Button
              key={intruder.id}
              color={intruder.color}
              position={intruder.position}
              isActive={false}
              isShowingSequence={gameState === 'showSequence'}
              isInvisible={false}
              isDecoy
              intruderId={intruder.id}
              onPress={handleButtonPress}
            />
          ))}

          {/* Rhythm Feedback Overlay */}
          {rhythmFeedback && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              padding: '16px 24px',
              background: rhythmFeedback.precision === 'perfect' ? 'rgba(147, 197, 114, 0.95)' :
                         rhythmFeedback.precision === 'good' ? 'rgba(249, 226, 175, 0.95)' :
                         rhythmFeedback.precision === 'ok' ? 'rgba(250, 179, 135, 0.95)' :
                         'rgba(255, 59, 92, 0.95)',
              color: 'white',
              borderRadius: '12px',
              fontSize: '20px',
              fontWeight: 700,
              textAlign: 'center',
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              pointerEvents: 'none',
              animation: 'rhythmFeedbackPop 0.5s ease-out',
              zIndex: 100
            }}>
              <div>{rhythmFeedback.precision === 'perfect' ? '✨ PERFECT!' :
                     rhythmFeedback.precision === 'good' ? '👍 GOOD!' :
                     rhythmFeedback.precision === 'ok' ? '✅ OK' :
                     '❌ MISS'}</div>
              <div style={{
                fontSize: '12px',
                marginTop: '4px',
                opacity: 0.9
              }}>
                {rhythmFeedback.delta < 1000 ? `${Math.round(rhythmFeedback.delta)}ms` : ''}
              </div>
            </div>
          )}

          {/* Panic Mode Indicator */}
          {panicMode && (
            <div style={{
              position: 'absolute',
              top: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              padding: '12px 20px',
              background: 'linear-gradient(135deg, rgba(255, 59, 92, 0.95), rgba(255, 149, 0, 0.95))',
              color: 'white',
              borderRadius: '24px',
              fontSize: '14px',
              fontWeight: 700,
              textAlign: 'center',
              boxShadow: '0 4px 16px rgba(255, 59, 92, 0.5)',
              pointerEvents: 'none',
              animation: 'panicPulse 1s ease-in-out infinite',
              zIndex: 100,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '18px' }}>🔥</span>
              <div>
                <div>PANIC MODE</div>
                <div style={{
                  fontSize: '10px',
                  opacity: 0.9,
                  marginTop: '2px'
                }}>
                  Streak: {panicStreak} | {Math.round((panicRef.current.getScoreMultiplier() - 1) * 100)}% Bonus
                </div>
              </div>
              <span style={{ fontSize: '18px' }}>🔥</span>
            </div>
          )}

          {/* Count-In Indicator (Rhythm Mode) */}
          {countInActive && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              padding: '24px 32px',
              background: 'rgba(147, 197, 114, 0.95)',
              color: 'white',
              borderRadius: '16px',
              fontSize: '28px',
              fontWeight: 700,
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
              pointerEvents: 'none',
              zIndex: 100,
              letterSpacing: '2px'
            }}>
              READY...
            </div>
          )}

          {/* Rhythm Timeline */}
          {engineRef.current.getConfig().enableRhythm && gameState === 'waitInput' && (
            <RhythmTimeline
              events={rhythmEngineRef.current.getTapTargets()}
              currentTime={rhythmCurrentTime}
              lookAheadMs={3000}
            />
          )}
        </div>
        
        <style>{`
          @keyframes rhythmFeedbackPop {
            0% { transform: translate(-50%, -50%) scale(0.8); opacity: 0; }
            50% { transform: translate(-50%, -50%) scale(1.1); opacity: 1; }
            100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
          }
          @keyframes panicPulse {
            0%, 100% { transform: translateX(-50%) scale(1); box-shadow: 0 4px 16px rgba(255, 59, 92, 0.5); }
            50% { transform: translateX(-50%) scale(1.05); box-shadow: 0 6px 24px rgba(255, 59, 92, 0.8); }
          }
        `}</style>

        {/* Status indicator */}
        {boardSize.width === 0 && (
          <div style={{
            padding: '10px 16px',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--muted)',
            minWidth: '200px',
            textAlign: 'center'
          }}>
            Preparing board...
          </div>
        )}

        {errorMessage && (
          <div role="alert" style={{
            padding: '10px 14px',
            background: 'rgba(255, 59, 92, 0.08)',
            border: '1px solid rgba(255, 59, 92, 0.3)',
            borderRadius: '8px',
            fontSize: '12px',
            color: '#ff9bae',
            maxWidth: '400px',
            textAlign: 'center'
          }}>
            {errorMessage}
          </div>
        )}

        <div style={{
          padding: '8px 16px',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '8px',
          fontSize: '12px',
          color: 'var(--muted)',
          minWidth: '200px',
          textAlign: 'center'
        }}>
          {gameState === 'showSequence' && (selectedMode === 'rhythm' ? 'Feel the rhythm...' : 'Watch the sequence...')}
          {gameState === 'waitInput' && (selectedMode === 'chaos'
            ? (invisibleMode ? 'Chaos mode: hover to reveal, avoid intruders...' : 'Chaos mode: avoid intruders!')
            : selectedMode === 'rhythm'
            ? (panicMode ? '🔥 Panic mode active! Keep the streak!' : 'Match the beat! Fast = Panic mode')
            : 'Your turn!')}
          {gameState === 'transition' && 'Great! Next level...'}
          {gameState === 'idle' && (
            selectedMode === 'rhythm'
              ? '💃 Rhythm Mode: Match timing, buttons wear out, fast play = panic!'
              : selectedMode === 'chaos'
              ? '🌀 Chaos Mode: Moving buttons, intruders, invisible mode!'
              : '🎮 Classic Mode: Pure Simon Says'
          )}
        </div>
      </div>

      {/* Game Over overlay */}
      {gameState === 'gameOver' && (
        <GameOverScreen
          score={score}
          highScore={highScore}
          level={level}
          mode={selectedMode}
          onRestart={handleRestart}
          onBackToModeSelect={onBackToModeSelect}
        />
      )}
    </>
  );
}
